/* trade.com.au storefront telemetry v1.5.1. Public project token, no secrets.
 * Collection posture (self-hosted PostHog, 21 September 2026): maximum surface area. Full autocapture,
 * unmasked replay with network and console capture, person profiles for every visitor, logged-in customer
 * identity, raw URLs, search text and click IDs. Everything stays gated on Shopify's analytics consent.
 * Collection endpoints are first-party: the Shopify app proxy at /apps/ph forwards to the self-hosted PostHog. */
(() => {
  'use strict';
  if (window.__tradeTelemetry || !['www.trade.com.au', 'trade.com.au', '55d3hd-ts.myshopify.com'].includes(location.hostname)) return;
  const status = window.__tradeTelemetry = { version: '1.5.1', state: 'waiting_for_consent' };
  const node = document.getElementById('trade-posthog-config');
  if (!node) return;
  let context;
  try { context = JSON.parse(node.textContent); } catch { status.state = 'invalid_context'; return; }
  const API_HOST = location.origin + '/apps/ph';
  const TOKEN = 'phc_r9DuTC6FEFZm24tADyQdDwaAiZWnCS6hLbTHuqLFnS2L';
  const RELEASE = 'telemetry-1.5.1';
  const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
  const clickKeys = ['gclid', 'gbraid', 'wbraid', 'dclid', 'fbclid', 'msclkid', 'ttclid', 'twclid', 'li_fat_id', 'sccid', 'epik', 'irclickid', 'yclid', 'rdt_cid'];
  const test = new URLSearchParams(location.search).get('telemetry_qa') === '1' ||
    !!(window.Shopify && Shopify.theme && Shopify.theme.role !== 'main');
  let ph, loading = false, firstPageCaptured = false, enabled = false;
  const BRIDGE_COOKIE = 'trade_ph_bridge';
  const cookieDomain = location.hostname.endsWith('trade.com.au') ? '; Domain=trade.com.au' : '';
  let lastBridgeWrite = 0;
  const numericId = value => /^\d{1,30}$/.test(String(value || '')) ? String(value) : undefined;
  const finite = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? Number(value) : undefined;
  const money = value => finite(value) === undefined ? undefined : Number(value) / 100;
  const text = value => typeof value === 'string' ? value : value === null || value === undefined ? undefined : String(value);
  // JSON-safe deep copy for theme payloads: drops DOM nodes, functions and cycles, bounds depth and size.
  function plain(value, depth = 0, seen = new WeakSet()) {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string') return value.length > 20000 ? value.slice(0, 20000) : value;
    if (typeof value === 'number' || typeof value === 'boolean') return value;
    if (typeof value !== 'object' || depth > 6 || (typeof Node !== 'undefined' && value instanceof Node)) return undefined;
    if (seen.has(value)) return undefined;
    seen.add(value);
    if (Array.isArray(value)) return value.slice(0, 250).map(item => plain(item, depth + 1, seen));
    const out = {};
    for (const [key, item] of Object.entries(value).slice(0, 200)) {
      const copy = plain(item, depth + 1, seen);
      if (copy !== undefined) out[key] = copy;
    }
    return out;
  }
  const recentInteractions = new Map();
  function searchCategories(value) {
    const raw = typeof value === 'string' ? value.slice(0, 500).toLowerCase() : '';
    const family = ['iphone', 'samsung', 'pixel', 'ipad', 'macbook', 'airpods'].find(word => new RegExp('\\b' + word + '\\b').test(raw)) || 'other_or_unknown';
    const model = raw.match(/\biphone[ -]*(se|[6-9]|[12][0-9])(?:\s*(pro\s*max|pro|plus|mini))?\b/);
    const storage = raw.match(/\b(16|32|64|128|256|512)\s*gb\b|\b([12])\s*tb\b/);
    return { search_query: typeof value === 'string' ? value : undefined, search_device_family: family,
      search_model: model ? 'iphone_' + model[1] + (model[2] ? '_' + model[2].replace(/\s+/g, '_') : '') : undefined,
      search_storage: storage ? storage[1] ? storage[1] + 'GB' : storage[2] + 'TB' : undefined };
  }
  function interaction(event, properties = {}) {
    if (!enabled || !allowed()) return;
    const key = event + JSON.stringify(properties), now = Date.now();
    if (now - (recentInteractions.get(key) || 0) < 750) return;
    recentInteractions.set(key, now);
    if (recentInteractions.size > 100) recentInteractions.delete(recentInteractions.keys().next().value);
    capture(event, properties);
  }
  function params() {
    const all = {};
    for (const [key, value] of new URLSearchParams(location.search)) all[key] = value;
    return all;
  }
  function acquisition() {
    if (!enabled || !ph || !allowed() || ph.getSessionProperty('traffic_channel')) return;
    const query = new URLSearchParams(location.search), tags = {};
    for (const key of campaignKeys) if (query.get(key)) tags['session_' + key] = query.get(key);
    for (const key of clickKeys) if (query.get(key)) tags['session_' + key] = query.get(key);
    const source = (tags.session_utm_source || '').toLowerCase();
    const medium = (tags.session_utm_medium || '').toLowerCase();
    const paid = /^(cpc|ppc|paid|paid[_-]?social|paid[_-]?search|display|cpm|retargeting)$/.test(medium);
    const meta = /^(facebook|fb|instagram|ig|meta)$/.test(source);
    const googleClick = ['gclid', 'gbraid', 'wbraid'].some(key => query.has(key));
    const microsoftClick = query.has('msclkid');
    let referrer = '';
    try { referrer = new URL(document.referrer).hostname.toLowerCase(); } catch { /* Direct visit. */ }
    if (referrer === 'trade.com.au' || referrer.endsWith('.trade.com.au') || referrer === '55d3hd-ts.myshopify.com') referrer = '';
    const search = /(^|\.)(google\.[a-z.]+|bing.com|duckduckgo.com|search.yahoo.com|ecosia.org)$/.test(referrer);
    const social = /(^|\.)(facebook.com|instagram.com|t.co|twitter.com|x.com|tiktok.com|pinterest.com)$/.test(referrer);
    const channel = paid && meta ? 'paid_meta' : (paid && source === 'google') || googleClick ? 'paid_google' :
      (paid && /^(bing|microsoft)$/.test(source)) || microsoftClick ? 'paid_microsoft' : paid && source === 'tiktok' ? 'paid_tiktok' :
      paid ? 'paid_other' : medium === 'email' ? 'email' : medium === 'organic' || (!source && search) ? 'organic_search' :
      meta || medium === 'social' || social ? 'social' : source || referrer ? 'referral' : 'direct_or_unknown';
    for (const [key, name] of [['campaign_id', 'ad_campaign_id'], ['adset_id', 'meta_adset_id'], ['adgroup_id', 'google_adgroup_id'], ['ad_id', 'ad_id']]) {
      const value = query.get(key);
      if (/^\d{1,30}$/.test(value || '')) tags[name] = value;
    }
    const entry = { ...tags, traffic_channel: channel,
      traffic_source: source || (googleClick ? 'google' : microsoftClick ? 'bing' : referrer || 'direct_or_unknown'),
      attribution_basis: source || medium ? 'utm' : googleClick ? 'google_click_marker' : microsoftClick ? 'microsoft_click_marker' : referrer ? 'referrer' : 'none',
      session_landing_path: location.pathname, session_landing_url: location.href, session_landing_query: params(),
      session_referrer: document.referrer || '', session_referrer_host: referrer,
      google_click_marker_present: googleClick, meta_click_marker_present: query.has('fbclid'),
      microsoft_click_marker_present: microsoftClick, tiktok_click_marker_present: query.has('ttclid'),
      attribution_schema: 'session-v3' };
    ph.register_for_session(entry);
    const stamp = new Date().toISOString();
    const touch = prefix => Object.fromEntries(Object.entries({ at: stamp, channel,
      source: entry.traffic_source, medium: tags.session_utm_medium || '',
      campaign: tags.session_utm_campaign || '', campaign_id: tags.ad_campaign_id || '',
      ad_id: tags.ad_id || '', landing_path: location.pathname, landing_url: location.href,
      referrer: document.referrer || '', click_id: clickKeys.map(key => query.get(key)).find(Boolean) || '',
    }).map(([key, value]) => [prefix + key, value]));
    const previous = ph.get_property('first_observed_at');
    ph.register_for_session({ visitor_observation: previous ? 'returning_observed' : 'first_observed' });
    if (!previous) ph.register(touch('first_observed_'));
    if (channel !== 'direct_or_unknown') ph.register(touch('last_nondirect_'));
    else {
      const last = Date.parse(ph.get_property('last_nondirect_at') || '');
      if (!Number.isFinite(last) || Date.now() - last > 30 * 86400000) {
        for (const key of Object.keys(touch('last_nondirect_'))) ph.unregister(key);
      }
    }
  }

  function updateBridge(force = false) {
    if (!enabled || !ph || !allowed() || (!force && Date.now() - lastBridgeWrite < 1000)) return;
    lastBridgeWrite = Date.now();
    try {
      const bridge = { v: 1, d: ph.get_distinct_id(), s: ph.get_session_id(), t: Date.now(), test };
      document.cookie = BRIDGE_COOKIE + '=' + encodeURIComponent(JSON.stringify(bridge)) + '; Path=/; Max-Age=1800; SameSite=Lax; Secure' + cookieDomain;
    } catch { /* Blocked storage must never interfere with shopping. */ }
  }

  function allowed() {
    try { return Shopify.customerPrivacy.analyticsProcessingAllowed() === true; }
    catch { return false; }
  }
  function capture(event, properties = {}) {
    if (enabled && ph && allowed()) { acquisition(); updateBridge(); ph.capture(event, properties); }
  }
  function productContext() {
    return {
      product_id: numericId(context.product_id), variant_id: numericId(context.variant_id),
      product_title: text(context.product_title), product_handle: text(context.product_handle),
      product_type: text(context.product_type), product_vendor: text(context.product_vendor),
      product_condition: text(context.product_condition), product_storage: text(context.product_storage),
      product_color: text(context.product_color), product_tags: context.product_tags, product_collections: context.product_collections,
      product_available: context.product_available, variant_available: context.variant_available,
      variant_title: text(context.variant_title), variant_sku: text(context.variant_sku),
      unit_price: money(context.price), compare_at_price: money(context.compare_at_price),
      variant_count: finite(context.variant_count), variants: context.variants,
    };
  }
  function pageEvents() {
    if (firstPageCaptured) return;
    firstPageCaptured = true;
    capture('$pageview');
    if (context.page_type === 'product') capture('storefront_product_viewed', productContext());
    if (context.page_type === 'collection') capture('storefront_collection_viewed', {
      collection_id: numericId(context.collection_id), collection_handle: text(context.collection_handle),
      collection_title: text(context.collection_title), products_count: finite(context.collection_products_count),
      sort_by: text(context.collection_sort_by), current_filters: params(),
    });
    if (context.page_type === 'cart') capture('storefront_cart_viewed', { item_count: finite(context.cart_item_count), cart_value: money(context.cart_total_price), cart: plain(context.cart) });
    if (context.page_type === 'search') capture('storefront_search_results_viewed', { results_count: context.search_results_count,
      ...searchCategories(new URLSearchParams(location.search).get('q')) });
    if (context.page_type === '404') capture('storefront_not_found', { path: location.pathname });
  }
  function identifyCustomer() {
    if (!numericId(context.customer_id)) return;
    const person = {
      customer_id: numericId(context.customer_id), email: text(context.customer_email),
      first_name: text(context.customer_first_name), last_name: text(context.customer_last_name),
      name: [context.customer_first_name, context.customer_last_name].filter(Boolean).join(' ') || undefined,
      phone: text(context.customer_phone), orders_count: finite(context.customer_orders_count),
      total_spent: money(context.customer_total_spent), tags: context.customer_tags,
      accepts_marketing: context.customer_accepts_marketing, has_account: context.customer_has_account,
      default_address_city: text(context.customer_city), default_address_province: text(context.customer_province),
      default_address_country: text(context.customer_country), default_address_zip: text(context.customer_zip),
      shop: 'trade.com.au',
    };
    for (const key of Object.keys(person)) if (person[key] === undefined) delete person[key];
    if (typeof ph.identify === 'function') ph.identify('shopify_customer:' + person.customer_id, person);
  }
  function activate() {
    if (!allowed()) return;
    enabled = true;
    ph.opt_in_capturing({ captureEventName: false });
    ph.set_config({ capture_heatmaps: true,
      capture_exceptions: { capture_unhandled_errors: true, capture_unhandled_rejections: true, capture_console_errors: true } });
    ph.register({ shop: 'trade.com.au', telemetry_version: status.version,
      storefront_release: RELEASE, storefront_theme_id: String(window.Shopify?.theme?.id || ''),
      storefront_theme_name: String(window.Shopify?.theme?.name || ''), storefront_theme_role: String(window.Shopify?.theme?.role || ''),
      event_source: 'shopify_theme', page_type: context.page_type, template: text(context.template), currency: context.currency,
      storefront_locale: text(context.locale), storefront_country: text(context.country), storefront_market: text(context.market),
      customer_logged_in: !!numericId(context.customer_id), cart_item_count: finite(context.cart_item_count),
      cart_token: text(context.cart_token), event_schema_version: 3, analytics_consent_state: 'allowed',
      is_test: test, environment: test ? 'preview_or_qa' : 'production' });
    identifyCustomer();
    ph.startSessionRecording(); // Self-hosted PostHog records 100% of eligible sessions.
    status.state = 'active';
    updateBridge(true);
    pageEvents();
  }
  function initSDK() {
    if (!allowed()) { loading = false; return; }
    if (!window.posthog || typeof window.posthog.init !== 'function') { loading = false; status.state = 'sdk_unavailable'; return; }
    window.posthog.init(TOKEN, {
      api_host: API_HOST, ui_host: 'https://tradecomau.system.style', defaults: '2026-05-30',
      persistence: 'localStorage+cookie', person_profiles: 'always',
      opt_out_persistence_by_default: true,
      capture_pageview: false, capture_pageleave: true,
      autocapture: { capture_copied_text: true },
      rageclick: true, capture_dead_clicks: true, capture_heatmaps: true,
      capture_exceptions: { capture_unhandled_errors: true, capture_unhandled_rejections: true, capture_console_errors: true },
      capture_performance: { web_vitals: true, network_timing: true, web_vitals_allowed_metrics: ['LCP', 'CLS', 'FCP', 'INP'] },
      enable_recording_console_log: true, logs: { captureConsoleLogs: true },
      disable_surveys: true, disable_session_recording: true,
      session_recording: {
        sampleRate: 1, maskAllInputs: false, maskInputOptions: { password: true },
        recordCrossOriginIframes: true, recordHeaders: true, recordBody: true, captureJsonLd: true, collectFonts: true,
        captureCanvas: { recordCanvas: true, canvasFps: 4, canvasQuality: '0.6' },
        compress_events: true,
      },
      before_send: event => {
        if (!enabled || !allowed()) return null;
        if (event.event !== '$snapshot') updateBridge();
        event.properties.is_test = test;
        event.properties.environment = test ? 'preview_or_qa' : 'production';
        if (event.event === '$exception' && test) event.properties.$exception_fingerprint = 'trade-telemetry-qa';
        return event;
      },
      loaded: instance => { ph = instance; ph.onSessionId(() => { acquisition(); updateBridge(true); }); activate(); },
    });
  }
  function consentChanged() {
    if (!allowed()) {
      enabled = false; status.state = 'consent_not_allowed';
      firstPageCaptured = false; recentInteractions.clear();
      document.cookie = BRIDGE_COOKIE + '=; Path=/; Max-Age=0; SameSite=Lax; Secure' + cookieDomain;
      if (ph) {
        ph.set_config({ capture_heatmaps: false, capture_exceptions: false });
        ph.stopSessionRecording(); ph.reset(true); ph.opt_out_capturing();
      }
      return;
    }
    if (ph) { if (!enabled) activate(); return; }
    if (loading) return;
    loading = true; status.state = 'loading';
    if (window.posthog && window.posthog.__loaded) { status.state = 'existing_sdk_detected'; return; }
    const script = document.createElement('script');
    script.src = API_HOST + '/static/array.js'; script.async = true; script.crossOrigin = 'anonymous';
    script.onload = initSDK;
    script.onerror = () => { loading = false; status.state = 'sdk_load_failed'; };
    document.head.appendChild(script);
  }

  // Facet filtering and sorting rewrite the URL without a page load; treat each as a pageview.
  let lastHref = location.href;
  function historyChanged() {
    if (location.href === lastHref) return;
    lastHref = location.href;
    capture('$pageview', { navigation_type: 'history_change', current_filters: params() });
  }
  if (typeof history !== 'undefined') for (const method of ['pushState', 'replaceState']) {
    const original = history[method];
    if (typeof original === 'function') history[method] = function (...args) { const result = original.apply(this, args); setTimeout(historyChanged, 0); return result; };
  }
  window.addEventListener('popstate', () => setTimeout(historyChanged, 0));

  // Dawn cart responses and errors, forwarded in full.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event.source === 'product-form') {
        const item = event.cartData || {};
        capture('storefront_product_added_to_cart', {
          variant_id: String(event.productVariantId || item.variant_id || ''),
          product_id: item.product_id ? String(item.product_id) : undefined,
          product_title: text(item.product_title), variant_title: text(item.variant_title), sku: text(item.sku),
          line_quantity: Number(item.quantity) || 1,
          unit_price: typeof item.price === 'number' ? item.price / 100 : undefined,
          line_properties: plain(item.properties), cart_item: plain({ ...item, sections: undefined }), cart_sections: event.cartData?.sections ? Object.keys(event.cartData.sections) : undefined,
        });
      } else if (event.source === 'cart-items') {
        const items = Array.isArray(event.cartData?.items) ? event.cartData.items : [];
        const changed = items.filter(item => String(item.variant_id || item.id) === String(event.variantId));
        capture('storefront_cart_updated', {
          variant_id: String(event.variantId || ''), item_count: event.cartData?.item_count,
          cart_value: typeof event.cartData?.total_price === 'number' ? event.cartData.total_price / 100 : undefined,
          cart_discount: typeof event.cartData?.total_discount === 'number' ? event.cartData.total_discount / 100 : undefined,
          cart_note: event.cartData?.note, cart_attributes: plain(event.cartData?.attributes), cart_token: event.cartData?.token,
          variant_remaining_quantity: Array.isArray(event.cartData?.items) ? changed.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0) : undefined,
          variant_removed: Array.isArray(event.cartData?.items) && numericId(event.variantId) ? !changed.length : undefined,
          items: items.slice(0, 250).map(item => ({ product_id: numericId(item.product_id),
            variant_id: numericId(item.variant_id || item.id), quantity: finite(item.quantity),
            product_title: text(item.product_title), variant_title: text(item.variant_title), sku: text(item.sku), handle: text(item.handle),
            unit_price: typeof item.final_price === 'number' ? item.final_price / 100 : undefined,
            line_price: typeof item.final_line_price === 'number' ? item.final_line_price / 100 : undefined,
            properties: plain(item.properties), discounts: plain(item.discounts) })),
        });
      } else {
        capture('storefront_cart_changed', { source: text(event.source), cart: plain({ ...(event.cartData || {}), sections: undefined }) });
      }
    });
    subscribe(PUB_SUB_EVENTS.cartError, event => capture('storefront_cart_error', {
      variant_id: String(event.productVariantId || ''), error_kind: 'shopify_cart_rejected',
      error_message: text(event.message), error_description: text(event.errors ?? event.description),
    }));
    if (PUB_SUB_EVENTS.variantChange) subscribe(PUB_SUB_EVENTS.variantChange, event => {
      const variant = event.data?.variant;
      if (!variant) return;
      capture('storefront_variant_selected', { product_id: String(event.data?.html?.querySelector?.('product-info')?.dataset?.productId || context.product_id || ''),
        variant_id: String(variant.id), variant_title: text(variant.title), sku: text(variant.sku), available: variant.available === true,
        unit_price: typeof variant.price === 'number' ? variant.price / 100 : undefined,
        compare_at_price: typeof variant.compare_at_price === 'number' ? variant.compare_at_price / 100 : undefined,
        options: plain(variant.options), variant: plain(variant) });
    });
    for (const [key, name] of Object.entries(PUB_SUB_EVENTS)) {
      if (['cartUpdate', 'cartError', 'variantChange'].includes(key)) continue;
      subscribe(name, event => capture('storefront_theme_' + String(key).replace(/[A-Z]/g, letter => '_' + letter.toLowerCase()), { detail: plain(event) }));
    }
  }
  document.addEventListener('toggle', event => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    const label = (details.querySelector('summary')?.textContent || '').trim();
    const inProduct = !!details.closest('product-info, .product');
    const category = ['shipping', 'delivery', 'warranty', 'return', 'condition', 'battery', 'specification'].find(key => label.toLowerCase().includes(key));
    if (inProduct && category) capture('storefront_product_information_opened', { category, label, product_id: String(context.product_id || '') });
    else interaction('storefront_disclosure_opened', { label, id: details.id || undefined, in_product: inProduct });
  }, true);
  document.addEventListener('change', event => {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.name === 'sort_by') {
      capture('storefront_sort_changed', { sort_by: target.value, collection_id: String(context.collection_id || '') });
    }
    if (!(target instanceof Element)) return;
    const filter = target.getAttribute('name') || '';
    if (target.closest('facet-filters-form') && /^filter\./.test(filter)) {
      const isOption = ['checkbox', 'radio'].includes(target.getAttribute('type')) || target instanceof HTMLSelectElement;
      interaction('storefront_filter_changed', { filter_key: filter, filter_value: text(target.value),
        selected: isOption ? (target instanceof HTMLSelectElement || target.checked === true) : undefined,
        price_bound: /^filter\.v\.price\.(gte|lte)$/.test(filter) ? finite(target.value) : undefined,
        collection_id: numericId(context.collection_id) });
    }
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof Element) || !enabled || !allowed()) return;
    const search = form.querySelector('input[name="q"]');
    if (search) interaction('storefront_search_submitted', { search_length_bucket:
      !search.value ? 'empty' : search.value.length <= 3 ? '1-3' : search.value.length <= 10 ? '4-10' : '11+',
      ...searchCategories(search.value),
      search_surface: form.closest('predictive-search') ? 'predictive_search' : 'search_form' });
    if (form.closest('product-form')) interaction('storefront_add_to_cart_clicked', {
      variant_id: numericId(form.querySelector('[name="id"]')?.value),
      requested_quantity: finite(form.querySelector('[name="quantity"]')?.value) ?? 1,
      surface: form.closest('quick-add-modal') ? 'quick_add' : context.page_type === 'product' ? 'product' : 'listing',
    });
    const action = form.getAttribute('action') || '';
    if (/\/contact/.test(action) || form.querySelector('input[name="form_type"][value="contact"], input[name="form_type"][value="customer"]')) {
      const fields = {};
      for (const field of form.querySelectorAll('input, textarea, select')) {
        const name = field.getAttribute('name');
        if (name && !/password|authenticity_token|utf8/.test(name)) fields[name] = field.value;
      }
      interaction('storefront_form_submitted', { form_action: action, form_type: text(form.querySelector('input[name="form_type"]')?.value), fields });
    }
  }, true);
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target.closest('a[href],button,input[name="checkout"]') : null;
    if (!target) return;
    const checkout = target.getAttribute('name') === 'checkout' || /^\/checkouts?(?:\/|\?|$)/.test(target.getAttribute('href') || '');
    if (checkout) capture('storefront_checkout_clicked', { cart_item_count: finite(context.cart_item_count), cart_value: money(context.cart_total_price) });
    if (!enabled || !allowed()) return;
    const href = target.getAttribute('href') || '';
    let url;
    try { url = new URL(href, location.origin); } catch { return; }
    const internal = ['trade.com.au', 'www.trade.com.au', location.hostname].includes(url.hostname);
    const product = internal && /\/(?:products)\/[a-z0-9-]+\/?$/.test(url.pathname);
    if (href && product) {
      const card = target.closest('.pcard-wrapper, .card-wrapper');
      const list = target.closest('#product-grid, product-recommendations, .product-grid, predictive-search');
      const cards = list ? Array.from(list.querySelectorAll('.pcard-wrapper, .card-wrapper')) : [];
      interaction('storefront_product_selected', { product_path: url.pathname, product_url: url.href,
        product_handle: url.pathname.split('/products/')[1]?.replace(/\/$/, ''), link_text: (target.textContent || '').trim().slice(0, 500),
        surface: target.closest('predictive-search') ? 'predictive_search' : target.closest('product-recommendations') ? 'recommendations' : card ? 'product_grid' : 'other_link',
        list_position: card && cards.includes(card) ? cards.indexOf(card) + 1 : undefined,
        collection_id: numericId(context.collection_id) });
    }
    const contact = /^(tel|mailto):/i.test(href) ? href.split(':')[0].toLowerCase() :
      /(^|\.)(wa.me|whatsapp.com)$/.test(url.hostname) ? 'whatsapp' :
      internal && /\/(pages\/)?contact(?:-us)?\/?$/.test(url.pathname) ? 'contact_page' : null;
    if (contact) interaction('storefront_contact_clicked', { contact_method: contact, contact_href: href });
    if (internal && /^\/(policies|pages)\/(?:shipping|refund|return|warranty|delivery|condition)[a-z-]*\/?$/.test(url.pathname)) {
      interaction('storefront_policy_clicked', { policy_path: url.pathname });
    }
    if (!internal && url.hostname) interaction('storefront_outbound_clicked', { outbound_url: url.href, outbound_host: url.hostname, link_text: (target.textContent || '').trim().slice(0, 500) });
    if (target.closest('facet-remove')) interaction('storefront_filter_removed', { collection_id: numericId(context.collection_id), filter_href: href });
    if (target.closest('media-gallery')) {
      const thumbnail = target.closest('[data-media-position]');
      if (thumbnail || ['next', 'previous'].includes(target.getAttribute('name'))) interaction('storefront_product_media_clicked', {
        product_id: numericId(context.product_id), media_position: finite(thumbnail?.getAttribute('data-media-position')),
        media_id: thumbnail?.getAttribute('data-media-id') || undefined, control: thumbnail ? 'thumbnail' : target.getAttribute('name') });
    }
  }, true);
  window.addEventListener('error', event => {
    if (event instanceof ErrorEvent) capture('storefront_browser_error', {
      error_kind: 'javascript_error', script_url: event.filename, line: event.lineno, column: event.colno,
      error_message: event.message, error_name: event.error?.name, error_stack: event.error?.stack,
    });
  });
  window.addEventListener('unhandledrejection', event => capture('storefront_browser_error', {
    error_kind: 'unhandled_promise_rejection', error_message: String(event?.reason?.message ?? event?.reason ?? ''), error_stack: event?.reason?.stack,
  }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') interaction('storefront_page_hidden', { path: location.pathname });
    else interaction('storefront_page_visible', { path: location.pathname });
  });
  document.addEventListener('visitorConsentCollected', consentChanged);
  if (window.Shopify?.customerPrivacy) consentChanged();
  else if (typeof window.Shopify?.loadFeatures === 'function') {
    Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
      if (error) { status.state = 'privacy_api_unavailable'; return; }
      consentChanged();
    });
  } else status.state = 'privacy_api_unavailable';
})();
